import React, { useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Select, Tooltip, Button, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COPY TO CLIPBOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faElevator, faStairs } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateFilters } from "../../../redux/reports/reportsSlice";
// FLATBUFFERS
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import formatUuidT from "../../../flatbuffers/display/uuid";
// CUSTOM HOOKS
import useGetAllBanksLight from "../../../customHooks/GET_requests/useGetAllBanksLight";
import useMonitoringSelectedBankIds from "../../../customHooks/useMonitoringSelectedBankIds";
// INTERFACES
import { RootState } from "../../../app/store";
import { BankLightInterface } from "../../../interfaces/Bank.interface";
import { ReportSliceFiltersInterface } from "../../../interfaces/Reports.interface";
// SCOPE
import { useScopePredicate } from "../../../customHooks/useScopedPermission";
import { PermissionAction } from "../../../flatbuffers/generated/archive/permission-action";

function ReportsFilterGetBanks() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Option, OptGroup } = Select;
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { filters, blockFilters } = useSelector((state: RootState) => state.report);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { banksLight, isLoadingGetAllBanksLight } = useGetAllBanksLight();
  const monitoringBankIds = useMonitoringSelectedBankIds();
  const bankScopeAllows = useScopePredicate(PermissionAction.VIEW);

  // ************************************************ */
  // FILTERED DATA ********************************** */
  const filteredBanks = useMemo(() => {
    let banks = banksLight;
    // Scope the picker to whatever the user has selected on the monitoring
    // dashboard's cascader. When nothing is selected we leave the full
    // org-scoped list intact so non-monitoring entry paths still work.
    if (monitoringBankIds) {
      banks = banks.filter((bank: BankLightInterface) =>
        monitoringBankIds.has(formatUuidT(bank.id)),
      );
    }
    // Apply per-bank VIEW scope: empty grant rows ⇒ allow-all.
    banks = banks.filter((bank: BankLightInterface) =>
      bankScopeAllows({ bankId: formatUuidT(bank.id) }),
    );
    if (filters.liftType === "ALL") return banks;
    return banks.filter(
      (bank: BankLightInterface) =>
        bank.type === LiftType[filters.liftType as keyof typeof LiftType],
    );
  }, [banksLight, filters.liftType, monitoringBankIds, bankScopeAllows]);

  // ************************************************ */
  // GROUPED DATA *********************************** */
  const serverGroups = useMemo(() => {
    const serverMap = new Map<string, { id: string; name: string }>();
    filteredBanks.forEach((bank: BankLightInterface) => {
      if (bank.channel?.server) {
        const sIdStr = formatUuidT(bank.channel.server.id);
        if (sIdStr && !serverMap.has(sIdStr)) {
          serverMap.set(sIdStr, { id: sIdStr, name: bank.channel.server.name });
        }
      }
    });
    return Array.from(serverMap.values());
  }, [filteredBanks]);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeBank = (newValue: string) => {
    const copy: ReportSliceFiltersInterface = structuredClone(filters);
    const bank = banksLight.find((b: BankLightInterface) => formatUuidT(b.id) === newValue);
    if (bank) {
      copy.bankId = formatUuidT(bank.id);
      copy.bankName = bank.name;
      if (bank.channel?.server) {
        copy.serverId = formatUuidT(bank.channel.server.id);
        copy.serverName = bank.channel.server.name;
      }
    }
    dispatch(updateFilters(copy));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (filteredBanks.length === 0) return;
    const currentInScope = filteredBanks.some(
      (bank: BankLightInterface) => formatUuidT(bank.id) === filters.bankId,
    );
    if (filters.bankId !== "" && currentInScope) return;
    const sorted = [...filteredBanks].sort((a: BankLightInterface, b: BankLightInterface) =>
      a.name.localeCompare(b.name),
    );
    onChangeBank(formatUuidT(sorted[0].id));
  }, [filteredBanks]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__width100" style={{ display: "flex", alignItems: "center" }}>
      <Button size="small" disabled style={{ minWidth: "fit-content" }}>
        {t("report.selectBank")}
      </Button>
      <Tooltip title={filteredBanks.length === 0 ? t("report.noBanks") : ""}>
        <Select
          className="generalStyles__width100"
          disabled={filteredBanks.length === 0 || blockFilters}
          showSearch
          optionFilterProp="label"
          placeholder={t("report.selectBank")}
          value={filters.bankId === "" ? null : filters.bankId}
          size="small"
          onChange={onChangeBank}
          loading={isLoadingGetAllBanksLight}
        >
          {serverGroups.map((serv) => (
            <OptGroup key={serv.id} label={`SERVER / ${serv.name}`}>
              {filteredBanks
                .filter(
                  (bank: BankLightInterface) => formatUuidT(bank.channel?.server?.id) === serv.id,
                )
                .sort((a: BankLightInterface, b: BankLightInterface) =>
                  a.name.localeCompare(b.name),
                )
                .map((bank: BankLightInterface) => (
                  <Option key={formatUuidT(bank.id)} value={formatUuidT(bank.id)} label={bank.name}>
                    <Tooltip
                      overlayInnerStyle={{ minWidth: "380px" }}
                      placement="right"
                      title={
                        <div>
                          <div style={{ marginBottom: "4px", fontSize: "13px", fontWeight: 600 }}>
                            Type
                          </div>
                          <div
                            style={{
                              borderBottom: "1px solid rgba(255,255,255,0.15)",
                              marginBottom: "6px",
                              paddingBottom: "6px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {bank.type === LiftType.ELEVATOR && (
                              <FontAwesomeIcon icon={faElevator} />
                            )}
                            {bank.type === LiftType.ESCALATOR && (
                              <FontAwesomeIcon icon={faStairs} />
                            )}
                            <b className="generalStyles__info">{LiftType[bank.type]}</b>
                          </div>
                          <div style={{ marginBottom: "4px", fontSize: "13px", fontWeight: 600 }}>
                            Bank
                          </div>
                          <div
                            style={{
                              marginBottom: "4px",
                              fontSize: "13px",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {bank.type === LiftType.ELEVATOR && (
                              <FontAwesomeIcon icon={faElevator} />
                            )}
                            {bank.type === LiftType.ESCALATOR && (
                              <FontAwesomeIcon icon={faStairs} />
                            )}
                            <b className="generalStyles__info">{bank.name}</b>
                          </div>
                          <div
                            style={{
                              borderBottom: "1px solid rgba(255,255,255,0.15)",
                              marginBottom: "6px",
                              paddingBottom: "6px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span>
                                <b className="generalStyles__info">ID:</b>{" "}
                                <span style={{ fontSize: "11px", opacity: 0.85 }}>
                                  {formatUuidT(bank.id)}
                                </span>
                              </span>
                              <CopyToClipboard
                                text={formatUuidT(bank.id)}
                                onCopy={() => message.success("Bank ID copied")}
                              >
                                <FontAwesomeIcon
                                  icon={faCopy}
                                  style={{
                                    cursor: "pointer",
                                    marginLeft: "8px",
                                    fontSize: "11px",
                                    opacity: 0.7,
                                  }}
                                />
                              </CopyToClipboard>
                            </div>
                          </div>

                          <div style={{ marginBottom: "4px", fontSize: "13px", fontWeight: 600 }}>
                            Channel
                          </div>
                          <div>
                            <b className="generalStyles__info">{bank.channel?.name}</b>
                          </div>
                          <div
                            style={{
                              borderBottom: "1px solid rgba(255,255,255,0.15)",
                              marginBottom: "6px",
                              paddingBottom: "6px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span>
                                <b className="generalStyles__info">ID:</b>{" "}
                                <span style={{ fontSize: "11px", opacity: 0.85 }}>
                                  {formatUuidT(bank.channel?.id)}
                                </span>
                              </span>
                              <CopyToClipboard
                                text={formatUuidT(bank.channel?.id)}
                                onCopy={() => message.success("Channel ID copied")}
                              >
                                <FontAwesomeIcon
                                  icon={faCopy}
                                  style={{
                                    cursor: "pointer",
                                    marginLeft: "8px",
                                    fontSize: "11px",
                                    opacity: 0.7,
                                  }}
                                />
                              </CopyToClipboard>
                            </div>
                          </div>
                          <div style={{ marginBottom: "4px", fontSize: "13px", fontWeight: 600 }}>
                            Server
                          </div>
                          <div>
                            <b className="generalStyles__info">{bank.channel?.server?.name}</b>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span>
                              <b className="generalStyles__info">ID:</b>{" "}
                              <span style={{ fontSize: "11px", opacity: 0.85 }}>
                                {formatUuidT(bank.channel?.server?.id)}
                              </span>
                            </span>
                            <CopyToClipboard
                              text={formatUuidT(bank.channel?.server?.id)}
                              onCopy={() => message.success("Server ID copied")}
                            >
                              <FontAwesomeIcon
                                icon={faCopy}
                                style={{
                                  cursor: "pointer",
                                  marginLeft: "8px",
                                  fontSize: "11px",
                                  opacity: 0.7,
                                }}
                              />
                            </CopyToClipboard>
                          </div>
                        </div>
                      }
                    >
                      {bank.type === LiftType.ELEVATOR && (
                        <FontAwesomeIcon icon={faElevator} style={{ marginRight: "8px" }} />
                      )}
                      {bank.type === LiftType.ESCALATOR && (
                        <FontAwesomeIcon icon={faStairs} style={{ marginRight: "8px" }} />
                      )}
                      {bank.name}
                    </Tooltip>
                  </Option>
                ))}
            </OptGroup>
          ))}
        </Select>
      </Tooltip>
    </div>
  );
}

export default ReportsFilterGetBanks;
